import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { publicApi } from '../services/api';
import { 
  Trophy, Users, Wallet, Zap, ArrowRight, CheckCircle2, 
  Shield, Globe, Sparkles, Play, Pause, Tv, Cloud, Newspaper,
  TrendingUp, DollarSign, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pvmmvvvw__krazy%20bets%20logo%20Final%20png1.png";
const RPS_PIC_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/aredzcap_RPS%20Pic.png";

const HERO_VIDEOS = [
  {
    url: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pzrkoo9c_Hero%20video%201.mp4",
    slogan: "Bet On Anything.",
    subtext: "Anytime.",
    cta: "Start Betting Now"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/8tyfmy30_Hero%20video%202.mp4",
    slogan: "Get Paid",
    subtext: "Instantly.",
    cta: "Claim Your Winnings"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/o0f4zf62_Hero%20video%203.mp4",
    slogan: "Invite Your Friends",
    subtext: "Let them put their money where their mouth is.",
    cta: "Challenge a Friend"
  }
];

const ABOUT_VIDEO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/twbvxxjl_Hero%20video%202%20%281%29.mp4";

// Demo bets for each category
const DEMO_BETS = {
  sports: [
    { user: "BetKing_ZA", bet: "Chiefs vs Pirates - Chiefs Win", amount: "R500", odds: "1.85x" },
    { user: "SoccerFan99", bet: "Man United Top 4 Finish", amount: "$100", odds: "2.10x" },
    { user: "LionBets", bet: "Springboks Beat All Blacks", amount: "R1000", odds: "1.65x" },
    { user: "SportsMaster", bet: "Liverpool Win Premier League", amount: "£250", odds: "3.20x" },
    { user: "WinnerTakes", bet: "Nadal Wins French Open", amount: "€150", odds: "2.40x" },
  ],
  entertainment: [
    { user: "CelebWatch", bet: "Taylor Swift Album #1", amount: "$50", odds: "1.25x" },
    { user: "MovieBuff", bet: "Marvel Movie Breaks $1B", amount: "$200", odds: "1.80x" },
    { user: "TVAddict", bet: "Stranger Things S5 Rating 9+", amount: "$75", odds: "2.50x" },
    { user: "AwardsPro", bet: "Beyoncé Wins Grammy", amount: "$100", odds: "1.40x" },
    { user: "StreamKing", bet: "Netflix Hits 300M Subs", amount: "$150", odds: "3.00x" },
  ],
  weather: [
    { user: "WeatherWiz", bet: "Cape Town Rain This Weekend", amount: "R200", odds: "2.80x" },
    { user: "StormChaser", bet: "JHB Hailstorm Before June", amount: "R350", odds: "4.50x" },
    { user: "SunSeeker", bet: "Durban 30°C+ Tomorrow", amount: "R100", odds: "1.90x" },
    { user: "CloudReader", bet: "London Fog Next Week", amount: "£50", odds: "1.75x" },
    { user: "TempTracker", bet: "NYC Heatwave in July", amount: "$80", odds: "1.60x" },
  ],
  news: [
    { user: "NewsHound", bet: "Tech IPO Before Q3", amount: "$500", odds: "2.20x" },
    { user: "PoliticalPro", bet: "New Policy Passes Senate", amount: "$300", odds: "1.95x" },
    { user: "MarketWatch", bet: "Bitcoin Hits $100K", amount: "$1000", odds: "3.50x" },
    { user: "GlobalBets", bet: "Peace Treaty Signed", amount: "€200", odds: "5.00x" },
    { user: "TrendSpotter", bet: "Viral Tweet Hits 10M", amount: "$25", odds: "8.00x" },
  ]
};

const LandingPage = () => {
  const [stats, setStats] = useState({
    total_bettors: 0,
    active_bets: 0,
    total_in_escrow: 0,
    categories: 8
  });
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRefs = useRef([]);
  const aboutVideoRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await publicApi.getStats();
        setStats(data);
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      }
    };
    fetchStats();
  }, []);

  // Auto-rotate hero videos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % HERO_VIDEOS.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Play current video
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideoIndex) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentVideoIndex]);

  const handlePlayAboutVideo = () => {
    if (aboutVideoRef.current) {
      if (isVideoPlaying) {
        aboutVideoRef.current.pause();
      } else {
        aboutVideoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />

      {/* Hero Video Carousel - NO LOGO, just words */}
      <section className="hero-video-container">
        {HERO_VIDEOS.map((video, index) => (
          <video
            key={index}
            ref={(el) => (videoRefs.current[index] = el)}
            className={`hero-video ${index === currentVideoIndex ? 'active' : ''}`}
            src={video.url}
            muted
            loop
            playsInline
          />
        ))}
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-2 text-white" style={{ fontFamily: 'Outfit' }}>
            {HERO_VIDEOS[currentVideoIndex].slogan}
          </h1>
          <p className="text-3xl md:text-5xl font-bold text-[#D4AF37] mb-8" style={{ fontFamily: 'Outfit' }}>
            {HERO_VIDEOS[currentVideoIndex].subtext}
          </p>
          
          <Link to="/register">
            <Button className="btn-primary text-lg px-10 py-5" data-testid="hero-cta-btn">
              {HERO_VIDEOS[currentVideoIndex].cta} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>

          {/* Carousel Dots */}
          <div className="flex gap-2 mt-8">
            {HERO_VIDEOS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentVideoIndex(index)}
                className={`carousel-dot ${index === currentVideoIndex ? 'active' : ''}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Live Stats Ticker */}
      <section className="bg-[#0F0F0F] py-3 overflow-hidden border-y border-[#D4AF37]/30">
        <div className="animate-ticker flex gap-8 whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-8 items-center">
              <span className="text-[#D4AF37] font-bold">{stats.total_bettors.toLocaleString()}+ BETTORS</span>
              <span className="text-[#28A745]">●</span>
              <span className="text-[#28A745] font-bold">${stats.total_in_escrow.toLocaleString()} IN ESCROW</span>
              <span className="text-[#D4AF37]">●</span>
              <span className="text-white font-bold">{stats.active_bets} ACTIVE BETS</span>
              <span className="text-[#28A745]">●</span>
              <span className="text-[#D4AF37] font-bold">98% PAYOUT RATE</span>
              <span className="text-white">●</span>
            </div>
          ))}
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black uppercase mb-6" style={{ fontFamily: 'Outfit' }}>
                About <span className="text-[#D4AF37]">Us</span>
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Welcome to <span className="text-[#D4AF37] font-bold">KrazyBets</span> — the world's first peer-to-peer digital betting platform where you can bet on anything, anytime. We've revolutionized the betting industry by removing the house and putting the power directly in your hands.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Whether it's sports, entertainment, weather forecasts, or viral trends — if you can think it and prove it, you can bet on it. Our secure escrow system ensures fair play, while instant payouts mean you get your winnings within minutes, not days.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Join thousands of bettors worldwide who are already backing their opinions with real money. No house odds. No fixed lines. Just you versus another person, with real stakes and real rewards.
              </p>
              <div className="flex gap-4">
                <Link to="/register">
                  <Button className="btn-primary">Join Now</Button>
                </Link>
                <Link to="/browse">
                  <Button className="btn-secondary">Explore Bets</Button>
                </Link>
              </div>
            </div>
            
            {/* Video Section */}
            <div className="relative">
              <div className="card-dark overflow-hidden aspect-video relative group cursor-pointer" onClick={handlePlayAboutVideo}>
                <video
                  ref={aboutVideoRef}
                  src={ABOUT_VIDEO_URL}
                  className="w-full h-full object-cover"
                  playsInline
                  loop
                />
                {/* Always-visible overlay that fades on hover when playing */}
                <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  isVideoPlaying ? 'bg-black/0 opacity-0 hover:opacity-100 hover:bg-black/30' : 'bg-black/40 opacity-100'
                }`}>
                  <button 
                    className="play-button"
                    data-testid="play-about-video"
                  >
                    {isVideoPlaying ? (
                      <Pause className="h-10 w-10 text-[#1A1A1A]" fill="#1A1A1A" />
                    ) : (
                      <Play className="h-10 w-10 text-[#1A1A1A] ml-1" fill="#1A1A1A" />
                    )}
                  </button>
                  <span className="absolute bottom-6 text-white font-bold text-lg">
                    {isVideoPlaying ? 'Pause' : 'Press Play'}
                  </span>
                </div>
                {/* Persistent small play/pause indicator in corner when video is playing */}
                {isVideoPlaying && (
                  <div className="absolute bottom-3 right-3 bg-black/60 rounded-full p-2 backdrop-blur-sm">
                    <Pause className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="stat-card" data-testid="stat-bettors">
              <div className="stat-icon">
                <Users className="h-6 w-6 text-[#1A1A1A]" />
              </div>
              <div className="stat-value">{stats.total_bettors.toLocaleString()}+</div>
              <div className="text-sm text-gray-400 uppercase font-semibold">Bettors</div>
            </div>
            <div className="stat-card" data-testid="stat-escrow">
              <div className="stat-icon">
                <Wallet className="h-6 w-6 text-[#1A1A1A]" />
              </div>
              <div className="stat-value">${stats.total_in_escrow.toLocaleString()}</div>
              <div className="text-sm text-gray-400 uppercase font-semibold">In Escrow</div>
            </div>
            <div className="stat-card" data-testid="stat-payout">
              <div className="stat-icon">
                <Zap className="h-6 w-6 text-[#1A1A1A]" />
              </div>
              <div className="stat-value">98%</div>
              <div className="text-sm text-gray-400 uppercase font-semibold">Payout Rate</div>
            </div>
            <div className="stat-card" data-testid="stat-categories">
              <div className="stat-icon">
                <Trophy className="h-6 w-6 text-[#1A1A1A]" />
              </div>
              <div className="stat-value">{stats.categories}</div>
              <div className="text-sm text-gray-400 uppercase font-semibold">Categories</div>
            </div>
          </div>
        </div>
      </section>

      {/* MODERATOR BANNER - NEW */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#28A745]/20 via-[#28A745]/10 to-[#28A745]/20">
        <div className="max-w-6xl mx-auto">
          <div className="card-dark border-[#28A745] border-2 overflow-hidden">
            <div className="md:flex items-center">
              <div className="p-8 md:w-2/3">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-6 w-6 text-[#D4AF37]" fill="#D4AF37" />
                  <span className="badge-gold">Earn Money</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  Subscribe as a <span className="text-[#28A745]">Moderator</span>
                </h2>
                <h3 className="text-xl text-[#D4AF37] font-bold mb-4">And Make Lots of Cash!</h3>
                <p className="text-gray-300 mb-4">
                  Moderators are the backbone of our peer-to-peer betting platform. They facilitate player vs player betting, 
                  ensuring fair play, verifying outcomes, and making sure every bet is resolved correctly.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                    <span>Earn <span className="text-[#D4AF37] font-bold">1% commission</span> on every bet you moderate</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                    <span>Verify Rock Paper Scissors webcam matches</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                    <span>Resolve disputes fairly and professionally</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                    <span>Build your reputation and earn more opportunities</span>
                  </li>
                </ul>
                <Link to="/register">
                  <Button className="btn-green text-lg">
                    Become a Moderator <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="p-8 md:w-1/3 bg-gradient-to-br from-[#28A745]/30 to-[#28A745]/10 flex flex-col items-center justify-center text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center mb-4">
                  <DollarSign className="h-12 w-12 text-[#1A1A1A]" />
                </div>
                <div className="text-4xl font-black text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>1%</div>
                <div className="text-white font-bold">Commission Per Bet</div>
                <div className="text-gray-400 text-sm mt-2">Premium subscribers only ($9.99/mo)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Banners with Live Bets */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black uppercase mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
            Live <span className="text-[#D4AF37]">Betting</span> Action
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            See what people are betting on right now across all categories
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Sports */}
            <CategoryBanner 
              icon={Trophy}
              title="Sports"
              color="#28A745"
              bets={DEMO_BETS.sports}
            />
            
            {/* Entertainment */}
            <CategoryBanner 
              icon={Tv}
              title="Entertainment"
              color="#D4AF37"
              bets={DEMO_BETS.entertainment}
            />
            
            {/* Weather */}
            <CategoryBanner 
              icon={Cloud}
              title="Weather"
              color="#17A2B8"
              bets={DEMO_BETS.weather}
            />
            
            {/* News */}
            <CategoryBanner 
              icon={Newspaper}
              title="News"
              color="#6F42C1"
              bets={DEMO_BETS.news}
            />
          </div>
        </div>
      </section>

      {/* RPS Section */}
      <section className="py-20 bg-gradient-to-b from-[#0F0F0F] to-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="badge-gold inline-flex items-center gap-2 mb-6">
                <span className="text-xl">✊✌️🖐️</span> LIVE GAMES
              </div>
              <h2 className="text-4xl md:text-5xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                Rock Paper<br />Scissors <span className="text-[#28A745]">Live</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Play real humans in real-time. Stake real money. Win instantly. 
                Choose from 3 game modes: commit-reveal (cheat-proof), board reveal, or live webcam.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Anti-cheat commit-reveal system</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Instant payouts to winner</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Global leaderboard</span>
                </li>
              </ul>
              <Link to="/register">
                <Button className="btn-green" data-testid="rps-cta-btn">
                  Play Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="card-dark p-0 overflow-hidden">
              <img src={RPS_PIC_URL} alt="Rock Paper Scissors Live" className="w-full h-full object-cover rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black uppercase mb-12 text-center" style={{ fontFamily: 'Outfit' }}>
            How It <span className="text-[#D4AF37]">Works</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-dark text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center text-2xl font-black text-[#1A1A1A]">
                1
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Create or Match</h3>
              <p className="text-gray-400">
                Create your own bet on anything or browse and match existing bets. Set your stake from $1 – max $10,000.
              </p>
            </div>
            <div className="card-dark text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center text-2xl font-black text-[#1A1A1A]">
                2
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Funds in Escrow</h3>
              <p className="text-gray-400">
                Both parties' funds are locked in secure escrow until the outcome is verified. No house, no tricks.
              </p>
            </div>
            <div className="card-dark text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#28A745] to-[#1E7E34] flex items-center justify-center text-2xl font-black text-white">
                3
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#28A745]" style={{ fontFamily: 'Outfit' }}>Instant Payout</h3>
              <p className="text-gray-400">
                Winner gets paid within minutes via PayShap, SEPA, UPI, or crypto. No delays, no hassle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Plans */}
      <section className="py-20 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-black uppercase mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
            Subscription <span className="text-[#D4AF37]">Plans</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">Choose the plan that fits your betting style</p>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="card-dark">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold uppercase text-white" style={{ fontFamily: 'Outfit' }}>Free</h3>
                <div className="text-4xl font-black text-[#D4AF37] mt-2">$0</div>
                <div className="text-gray-500 text-sm">forever</div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>1 bet/day</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>10% platform fee</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Browse all bets</span>
                </li>
              </ul>
              <Link to="/register" className="block">
                <Button className="btn-secondary w-full">Get Started</Button>
              </Link>
            </div>
            
            {/* Basic */}
            <div className="card-dark relative border-[#D4AF37] border-2">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="badge-gold">Popular</span>
              </div>
              <div className="text-center mb-6 mt-2">
                <h3 className="text-xl font-bold uppercase text-white" style={{ fontFamily: 'Outfit' }}>Basic</h3>
                <div className="text-4xl font-black text-[#D4AF37] mt-2">$4.99</div>
                <div className="text-gray-500 text-sm">per month</div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>5 bets/day</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>5% platform fee</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Priority listing</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Verified badge</span>
                </li>
              </ul>
              <Link to="/register" className="block">
                <Button className="btn-primary w-full">Subscribe</Button>
              </Link>
            </div>
            
            {/* Premium */}
            <div className="card-dark">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold uppercase text-white" style={{ fontFamily: 'Outfit' }}>Premium</h3>
                <div className="text-4xl font-black text-[#28A745] mt-2">$9.99</div>
                <div className="text-gray-500 text-sm">per month</div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Unlimited bets</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>3% platform fee</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Become Moderator</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-[#28A745]" />
                  <span>Earn 1% of others</span>
                </li>
              </ul>
              <Link to="/register" className="block">
                <Button className="btn-green w-full">Subscribe</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Qbean Token Banner */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#1a1a6e]/40 via-[#2a2a8e]/30 to-[#1a1a6e]/40">
        <div className="max-w-6xl mx-auto">
          <div className="card-dark border-[#6366F1] border-2 overflow-hidden">
            <div className="md:flex items-center">
              <div className="p-8 md:w-2/3">
                <h2 className="text-3xl md:text-4xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  Bet with your <span className="text-[#8B8BF5]">Qbean Tokens (QBC)</span> and save <span className="text-[#D4AF37]">50%</span> on each BET
                </h2>
                <p className="text-gray-300 text-lg mb-4">
                  Sign up and receive <span className="text-[#D4AF37] font-bold">100 free Qbean Tokens</span>. You can also buy Qbean tokens from major crypto exchanges directly from your wallet.
                </p>
                <Link to="/register">
                  <Button className="btn-primary text-lg" data-testid="qbean-signup-btn">
                    Sign Up & Get 100 QBC Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="p-8 md:w-1/3 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/d2e0wwjx_2.png" 
                  alt="Qbean Token" 
                  className="w-40 h-40 md:w-48 md:h-48 drop-shadow-[0_0_30px_rgba(99,102,241,0.5)]"
                  data-testid="qbean-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
                <Shield className="h-8 w-8 text-[#1A1A1A]" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Safe & Compliant</h3>
              <p className="text-gray-400">FICA/AML compliant. All users age-verified (18+). All bets reviewed.</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#28A745] to-[#1E7E34] flex items-center justify-center">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#28A745]" style={{ fontFamily: 'Outfit' }}>Global Payouts</h3>
              <p className="text-gray-400">PayShap, SEPA, UPI, Pix, Visa Direct, Mastercard Send, and crypto.</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
                <Zap className="h-8 w-8 text-[#1A1A1A]" />
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Instant Settlements</h3>
              <p className="text-gray-400">Payouts processed within 1 minute to 1 hour worldwide.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#D4AF37] via-[#F4D03F] to-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black uppercase text-[#1A1A1A] mb-6" style={{ fontFamily: 'Outfit' }}>
            Ready to Back Your Opinions?
          </h2>
          <p className="text-[#1A1A1A]/80 text-lg mb-8">
            Join thousands of bettors worldwide. Free 100 Qbean tokens on signup.
          </p>
          <Link to="/register">
            <Button className="bg-[#1A1A1A] text-[#D4AF37] px-10 py-5 text-lg font-bold uppercase rounded-lg shadow-lg hover:bg-[#2D2D2D] transition-all" data-testid="cta-signup-btn">
              Create Free Account <ArrowRight className="ml-2 h-5 w-5 inline" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Category Banner Component
const CategoryBanner = ({ icon: Icon, title, color, bets }) => {
  return (
    <div className="card-dark overflow-hidden">
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)` }}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h3 className="font-bold text-xl uppercase" style={{ fontFamily: 'Outfit', color }}>{title}</h3>
        <span className="badge-green ml-auto">LIVE</span>
      </div>
      
      {/* Scrolling Bets */}
      <div className="overflow-hidden">
        <div className="bet-ticker">
          {[...bets, ...bets].map((bet, idx) => (
            <div key={idx} className="bet-ticker-item">
              <span className="text-[#D4AF37] font-semibold">@{bet.user}</span>
              <span className="text-gray-400">|</span>
              <span className="text-white">{bet.bet}</span>
              <span className="text-[#28A745] font-bold">{bet.amount}</span>
              <span className="text-gray-500">{bet.odds}</span>
            </div>
          ))}
        </div>
      </div>
      
      <Link to="/browse" className="block mt-4">
        <Button className="btn-secondary w-full text-sm">View All {title} Bets</Button>
      </Link>
    </div>
  );
};

export default LandingPage;
